import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EstadoError, EstadoVacio, ListaEsqueleto } from '../../components/ui/Estados';
import { Input, Select } from '../../components/ui/Input';
import { Tabla, type Columna } from '../../components/ui/Tabla';
import { BarraFiltros, type ChipFiltro } from '../../components/layout/BarraFiltros';
import { CabeceraPagina } from '../../components/layout/CabeceraPagina';
import {
  repositorioEnergiaUmbrales,
  type DatosUmbralEnergia,
  type UmbralEnergia,
} from '../../../data/repositories/RepositorioEnergiaUmbrales';
import { repositorioMedidores } from '../../../data/repositories/RepositorioMedidores';
import { describirVariable } from '../../../domain/value-objects/VariableMedidor';
import { ErrorApp, registrarError } from '../../../domain/errores';
import { useSesionStore } from '../../stores/sesionStore';
import { FormularioUmbralEnergia } from './FormularioUmbralEnergia';
import estilos from './Umbrales.module.css';
import { FichaRegistro } from '../../components/layout/FichaRegistro';

export function UmbralesEnergia() {
  const clienteConsultas = useQueryClient();
  const politica = useSesionStore((s) => s.politica);
  const puedeGestionar = politica?.puede('gestionar_umbrales') ?? false;

  const [busqueda, setBusqueda] = useState('');

  // Registro cuya ficha de consulta esta abierta.
  const [enFicha, setEnFicha] = useState<UmbralEnergia | null>(null);
  const [medidor, setMedidor] = useState('');
  const [estado, setEstado] = useState<'todos' | 'activos' | 'inactivos'>('todos');
  const [formularioAbierto, setFormularioAbierto] = useState(false);
  const [enEdicion, setEnEdicion] = useState<UmbralEnergia | null>(null);

  const consulta = useQuery({
    queryKey: ['umbrales-energia'],
    queryFn: () => repositorioEnergiaUmbrales.listarUmbrales(),
  });

  const medidores = useQuery({
    queryKey: ['medidores', null],
    queryFn: () => repositorioMedidores.listar(null),
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ['umbrales-energia'] });
  };

  const guardar = useMutation({
    mutationFn: (datos: DatosUmbralEnergia) =>
      enEdicion
        ? repositorioEnergiaUmbrales.actualizarUmbral(enEdicion.id, datos)
        : repositorioEnergiaUmbrales.crearUmbral(datos),
    onSuccess: () => {
      toast.success(enEdicion ? 'Umbral actualizado' : 'Umbral creado');
      setFormularioAbierto(false);
      setEnEdicion(null);
      refrescar();
    },
    onError: (e) => {
      registrarError('umbralesEnergia.guardar', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo guardar el umbral');
    },
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => repositorioEnergiaUmbrales.eliminarUmbral(id),
    onSuccess: () => {
      toast.success('Umbral eliminado');
      refrescar();
    },
    onError: (e) => {
      registrarError('umbralesEnergia.eliminar', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo eliminar el umbral');
    },
  });

  const alternarActivo = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) =>
      repositorioEnergiaUmbrales.cambiarActivoUmbral(id, activo),
    onSuccess: refrescar,
    onError: (e) => {
      registrarError('umbralesEnergia.cambiarActivo', e);
      toast.error('No se pudo cambiar el estado del umbral');
    },
  });

  const umbrales = useMemo(() => consulta.data ?? [], [consulta.data]);

  const nombresMedidor = useMemo(
    () => [...new Set(umbrales.map((u) => u.medidorNombre))].sort(),
    [umbrales],
  );

  const filtrados = useMemo(
    () =>
      umbrales.filter((u) => {
        if (medidor && u.medidorNombre !== medidor) return false;
        if (estado === 'activos' && !u.activo) return false;
        if (estado === 'inactivos' && u.activo) return false;

        const t = busqueda.trim().toLowerCase();
        if (!t) return true;

        return [
          u.medidorNombre,
          u.variableEtiqueta,
          describirVariable(u.variable).etiqueta,
          u.descripcion,
        ].some((campo) => campo?.toLowerCase().includes(t));
      }),
    [umbrales, busqueda, medidor, estado],
  );

  const chips: ChipFiltro[] = [];
  if (medidor) {
    chips.push({ clave: 'medidor', etiqueta: `Medidor: ${medidor}`, quitar: () => setMedidor('') });
  }
  if (estado !== 'todos') {
    chips.push({
      clave: 'estado',
      etiqueta: estado === 'activos' ? 'Solo activos' : 'Solo inactivos',
      quitar: () => setEstado('todos'),
    });
  }
  if (busqueda.trim()) {
    chips.push({
      clave: 'busqueda',
      etiqueta: `Búsqueda: «${busqueda.trim()}»`,
      quitar: () => setBusqueda(''),
    });
  }

  const columnas: Columna<UmbralEnergia>[] = [
    {
      clave: 'medidor',
      encabezado: 'Medidor',
      movil: 'titulo',
      render: (u) => <span className={estilos.sensor}>{u.medidorNombre}</span>,
    },
    {
      clave: 'variable',
      encabezado: 'Magnitud vigilada',
      movil: 'subtitulo',
      render: (u) => u.variableEtiqueta ?? describirVariable(u.variable).etiqueta,
    },
    {
      clave: 'rango',
      encabezado: 'Rango permitido',
      render: (u) => {
        const { unidad } = describirVariable(u.variable);
        const sufijo = unidad ? ` ${unidad}` : '';
        const { valorMinimo: min, valorMaximo: max } = u;

        const texto =
          min !== null && max !== null
            ? `${min} – ${max}${sufijo}`
            : min !== null
              ? `≥ ${min}${sufijo}`
              : max !== null
                ? `≤ ${max}${sufijo}`
                : '—';

        return <span className={estilos.rango}>{texto}</span>;
      },
    },
    {
      clave: 'criticidad',
      encabezado: 'Gravedad',
      movil: 'detalle',
      render: (u) => <Badge color={u.criticidad.color}>{u.criticidad.etiqueta}</Badge>,
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      movil: 'detalle',
      render: (u) =>
        u.activo ? (
          <Badge color="var(--status-success)">Activo</Badge>
        ) : (
          <Badge color="var(--text-3)">Inactivo</Badge>
        ),
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      movil: 'accion',
      render: (u) => (
        <div className={estilos.acciones}>
          <Button
            tamano="sm"
            variante="secundario"
            icono={u.activo ? 'interruptorOn' : 'interruptorOff'}
            title={u.activo ? 'Desactivar' : 'Activar'}
            motivoDeshabilitado={
              puedeGestionar ? null : 'Tu rol no puede modificar la configuración'
            }
            onClick={() => alternarActivo.mutate({ id: u.id, activo: !u.activo })}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="ver"
            title="Ver datos"
            onClick={() => setEnFicha(u)}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="editar"
            title="Editar"
            motivoDeshabilitado={
              puedeGestionar ? null : 'Tu rol no puede modificar la configuración'
            }
            onClick={() => {
              setEnEdicion(u);
              setFormularioAbierto(true);
            }}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="eliminar"
            title="Eliminar"
            motivoDeshabilitado={
              puedeGestionar ? null : 'Tu rol no puede modificar la configuración'
            }
            onClick={() => {
              const seguro = window.confirm(
                `¿Eliminar este umbral de «${u.medidorNombre}»?\n\n` +
                  'Esa magnitud dejará de vigilarse.',
              );
              if (seguro) eliminar.mutate(u.id);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className={estilos.pagina}>
      <CabeceraPagina
        titulo="Umbrales de energía"
        descripcion="Límites sobre las magnitudes que reportan los medidores."
        acciones={
          <>
            <Button
              variante="secundario"
              icono="refrescar"
              compactoEnMovil
              onClick={refrescar}
              cargando={consulta.isFetching}
            >
              Actualizar
            </Button>
            <Button
              icono="agregar"
              compactoEnMovil
              motivoDeshabilitado={
                puedeGestionar ? null : 'Tu rol no puede modificar la configuración'
              }
              onClick={() => {
                setEnEdicion(null);
                setFormularioAbierto(true);
              }}
            >
              Nuevo umbral
            </Button>
          </>
        }
      />

      <BarraFiltros
        chips={chips}
        acciones={
          chips.length > 0 && (
            <Button
              variante="fantasma"
              icono="cerrar"
              onClick={() => {
                setMedidor('');
                setEstado('todos');
                setBusqueda('');
              }}
            >
              Limpiar filtros
            </Button>
          )
        }
      >
        <Select
          etiqueta="Medidor"
          placeholder="Todos los medidores"
          value={medidor}
          onChange={(e) => setMedidor(e.target.value)}
          opciones={nombresMedidor.map((n) => ({ valor: n, etiqueta: n }))}
        />
        <Input
          etiqueta="Buscar"
          icono="buscar"
          placeholder="Magnitud o descripción"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <Select
          etiqueta="Estado"
          value={estado}
          onChange={(e) => setEstado(e.target.value as typeof estado)}
          opciones={[
            { valor: 'todos', etiqueta: 'Todos' },
            { valor: 'activos', etiqueta: 'Solo activos' },
            { valor: 'inactivos', etiqueta: 'Solo inactivos' },
          ]}
        />
      </BarraFiltros>

      {consulta.isLoading ? (
        <ListaEsqueleto filas={5} />
      ) : consulta.isError ? (
        <EstadoError
          acciones={
            <Button variante="secundario" icono="refrescar" onClick={refrescar}>
              Reintentar
            </Button>
          }
        />
      ) : filtrados.length === 0 ? (
        <EstadoVacio
          icono="umbrales"
          titulo={umbrales.length === 0 ? 'Sin umbrales de energía' : 'Ninguno coincide'}
          descripcion={
            umbrales.length === 0
              ? 'Define límites de tensión, corriente o consumo para que el sistema avise cuando se salgan de rango.'
              : 'Prueba a cambiar la búsqueda o los filtros.'
          }
        />
      ) : (
        <>
          <Tabla
            datos={filtrados}
            columnas={columnas}
            claveFila={(u) => u.id}
            etiqueta="Umbrales de energía"
          />

          <FichaRegistro
            item={enFicha}
            titulo="Umbral de energía"
            columnas={columnas}
            onCerrar={() => setEnFicha(null)}
          />
        </>
      )}

      <FormularioUmbralEnergia
        abierto={formularioAbierto}
        umbral={enEdicion}
        medidores={medidores.data ?? []}
        guardando={guardar.isPending}
        onCerrar={() => {
          setFormularioAbierto(false);
          setEnEdicion(null);
        }}
        onGuardar={(datos) => guardar.mutate(datos)}
      />
    </div>
  );
}
